import React, { useState, useEffect } from 'react';
import { Modal, Empty, useFlash, F, Confirm } from '../components/UI';
import { Plus, Download, Trash, X } from '../components/Icons';
import CustomerSearch from '../components/CustomerSearch';
import { inventoryApi } from '../api/client';
import { useAuth } from '../context/AuthContext';

/* ── localStorage ──────────────────────────────────────────────── */
const LS_KEY  = 'sakthi_quotations';
const loadAll = () => { try { return JSON.parse(localStorage.getItem(LS_KEY) || '[]'); } catch { return []; } };
const saveAll = list => localStorage.setItem(LS_KEY, JSON.stringify(list));

/* ── Quote number helpers ──────────────────────────────────────── */
function nextQuoteNo(list) {
  const now = new Date();
  const yy  = String(now.getFullYear()).slice(2);
  const mm  = String(now.getMonth() + 1).padStart(2, '0');
  const dd  = String(now.getDate()).padStart(2, '0');
  const pfx = `QUO-${yy}${mm}${dd}-`;
  const n   = list.filter(q => q.id.startsWith(pfx)).length;
  return `${pfx}${String(n + 1).padStart(3, '0')}`;
}

function nextSolarRefNo() {
  const n = Math.floor(100 + Math.random() * 900);
  return `REF/S${n}/2026`;
}

/* ── Helpers ───────────────────────────────────────────────────── */
const today   = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; };
const fmtDate = d => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { dateStyle: 'medium' }) : '—';

function calcTotals(q) {
  const sub  = (q.items || []).reduce((a, i) => a + (Number(i.qty) || 0) * (Number(i.unit_price) || 0), 0);
  const disc = Math.min(Number(q.disc_val) || 0, sub);
  return { sub, disc, total: sub - disc };
}

function amountInWords(amount) {
  const a = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine','Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen'];
  const b = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
  function iw(n) {
    if (n < 20)     return a[n];
    if (n < 100)    return b[Math.floor(n/10)] + (n%10 ? ' '+a[n%10] : '');
    if (n < 1000)   return a[Math.floor(n/100)] + ' Hundred' + (n%100 ? ' '+iw(n%100) : '');
    if (n < 100000) return iw(Math.floor(n/1000)) + ' Thousand' + (n%1000 ? ' '+iw(n%1000) : '');
    if (n < 10000000) return iw(Math.floor(n/100000)) + ' Lakh' + (n%100000 ? ' '+iw(n%100000) : '');
    return iw(Math.floor(n/10000000)) + ' Crore' + (n%10000000 ? ' '+iw(n%10000000) : '');
  }
  if (!amount) return 'Zero Only';
  return iw(Math.round(amount)) + ' Only';
}

/* ── Status pill ───────────────────────────────────────────────── */
const STATUS_STYLE = {
  draft:     { background: '#e5e7eb', color: '#374151' },
  sent:      { background: '#dbeafe', color: '#1d4ed8' },
  converted: { background: '#d1fae5', color: '#065f46' },
  expired:   { background: '#fee2e2', color: '#991b1b' },
};
function QPill({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.draft;
  return <span style={{ ...s, borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 600 }}>{status}</span>;
}

/* ── Default solar items ───────────────────────────────────────── */
const DEFAULT_SOLAR_ITEMS = [
  { item_name: 'Solar Photo Voltaic Module',         description: '• Mono Perc Bifacial ADANI/WAAREE DCR PANELS\n• 550/560-watts X 6Nos\n• 30-year Performance warranty',                                                                                            quantity: '3KW or Equivalent' },
  { item_name: 'Grid-Tie Inverter',                  description: '• 3Kw Grid Tie Inverter\n• Make-FOXESS/MICROTEK\n• 7Year Product Warranty\n• Single phase Machine',                                                                                               quantity: '1 nos' },
  { item_name: 'Mounting Structure',                 description: '• Structural Sections (18SWG3X1.5) (APPOLO)\n• Structure only for flat roof\n• Concrete Foundation\n• SS304 Grade Nuts and Bolts\n• 30mm Aluminum Mid & End Clamps',                              quantity: 'As required' },
  { item_name: 'DC Combiner Box',                   description: '• IP68 Enclosure\n• DC SPD per string (Havells/VGUARD/Merson/L&T)\n• DC Isolator (Havells/VGUARD) per string\n• 32A Fuses Per String (Havells/VGUARD)\n• IP68 Cable Glands',                     quantity: '1 nos' },
  { item_name: 'AC Combiner Box',                   description: '• IP68 Enclosure\n• AC SPD (Havells/VGUARD)\n• AC MCB (Havells/VGUARD)\n• IP68 Cable Glands\n• Manual isolator 62amp (Havells/V-Guard)',                                                         quantity: '1 nos' },
  { item_name: 'DC Cable',                          description: '• 4/6sq.mm 1.5kV UV DC cable\n• Tinned Cu, Stranded (POLYCAB/APPAR)\n• High Grade PVC Conduits.',                                                                                               quantity: 'As required' },
  { item_name: 'AC Cable',                          description: '• 6Sqmm 1.2kV Fire retardant\n• High Grade PVC conduit (POLYCAB/FINOLEX/BONTON/APPAR)',                                                                                                          quantity: 'As required' },
  { item_name: 'Lightning Arrester',                description: '• Multi spike lightning arrester\n• 10SWG COPPER\n• 250µ Cu Bounded Rod electrode (Excel)\n• Earthing compound (Excel)',                                                                         quantity: '1 nos' },
  { item_name: 'MC4 Connector',                     description: 'Staubli',                                                                                                                                                                                        quantity: '4 nos' },
  { item_name: 'Earthing',                          description: '• 16sq Aluminum for Panel Body\n• 10SWG, Copper for AC/DC\n• 250µ Cu, Bounded Rod electrode (Excel)\n• Earthing compound (Excel)',                                                              quantity: '3 nos' },
  { item_name: 'Energy Meter',                      description: 'Single Phase (L & T GENUS, VISIONTEK)',                                                                                                                                                          quantity: '1 nos' },
];

/* ── SOLAR PDF ─────────────────────────────────────────────────── */
function printSolarQuotation(q) {
  const dateStr      = fmtDate(q.quote_date || (q.created_at || '').split('T')[0]);
  const kw           = q.solar_kw || '3';
  const panelBrand   = q.solar_panel_brand   || 'ADANI/WAAREE DCR PANELS';
  const invBrand     = q.solar_inverter_brand || 'FOXESS/MICROTEK';
  const totalAmt     = Number(q.solar_total_amount  || 0);
  const subsidyAmt   = Number(q.solar_subsidy       || 78000);
  const ksebCharges  = Number(q.solar_kseb_charges  || 3540);

  const itemRows = (q.solar_items || DEFAULT_SOLAR_ITEMS).map((item, i) => `
    <tr>
      <td style="padding:8px;border:1px solid #ccc;text-align:center;vertical-align:top;color:#555">${i+1}</td>
      <td style="padding:8px;border:1px solid #ccc;text-align:center;vertical-align:top;font-weight:500">${item.item_name||'—'}</td>
      <td style="padding:8px;border:1px solid #ccc;vertical-align:top;white-space:pre-line;font-size:11.5px">${item.description||''}</td>
      <td style="padding:8px;border:1px solid #ccc;text-align:center;vertical-align:top">${item.quantity||''}</td>
    </tr>`).join('');

  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"/>
<title>Solar Quotation - ${q.customer_name}</title>
<style>
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:Arial,sans-serif;font-size:13px;color:#111;padding:30px 40px;max-width:820px;margin:0 auto}
.hdr{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #8ab800;padding-bottom:14px;margin-bottom:22px}
.co{font-size:22px;font-weight:900;color:#1a2744;letter-spacing:1px}
.co-sub{font-size:12px;color:#555;margin-top:2px}
.co-right{text-align:right;font-size:11px;color:#333;line-height:1.7}
.q-title{text-align:center;font-size:20px;font-weight:700;letter-spacing:2px;margin:18px 0;text-decoration:underline}
.ref-date{display:flex;justify-content:space-between;margin-bottom:18px;font-size:13px}
.to-blk{margin-bottom:16px;line-height:1.8}
p{margin-bottom:10px;line-height:1.7}
.hl{font-weight:700;font-size:13px;margin:10px 0 8px}
.hl ul{margin-left:24px;line-height:2.2;font-weight:700}
.sec{font-weight:700;font-size:13px;margin:18px 0 8px;text-decoration:underline}
.gls,.grd{margin-left:20px;line-height:2;font-size:12.5px;margin-bottom:6px}
.est-title{text-align:center;font-weight:700;font-size:14px;margin:22px 0 12px}
table{width:100%;border-collapse:collapse;font-size:12.5px}
.hrow{background:#8ab800;color:#fff}
.hrow th{padding:9px 8px;border:1px solid #7a9e00;text-align:center}
.srow{background:#8ab800;color:#fff}
.srow td{padding:7px 8px;text-align:center;font-weight:700;border:1px solid #7a9e00}
.trow td{font-weight:700;font-size:13px;text-align:center;padding:9px 8px;border:1px solid #ccc;background:#f5f5f5}
.ebox{border:2px solid #333;padding:14px 18px;margin:18px 0;background:#fff8f0;border-radius:4px;font-size:12.5px;line-height:2}
.bbox{border:2px solid #333;padding:12px 16px;margin:14px 0;display:inline-block;font-size:13px;line-height:1.9}
.ftr{margin-top:22px;border-top:1px solid #ccc;padding-top:12px;text-align:center;font-size:11.5px;color:#444;line-height:2}
.sig{text-align:right;margin-top:20px;font-size:13px}
@media print{body{padding:15px 20px}}
</style></head><body>
<div class="hdr">
  <div><div class="co">SAKTHI INVERTERS</div><div class="co-sub">Since 1995</div></div>
  <div class="co-right"><strong>AMBALATH ENTERPRISES</strong><br/>GSTIN: 32AFNPN5476M1ZT<br/>East Nada, Sree Mahadevar Temple<br/>Kandiyoor, Mavelikara, Pin-690103</div>
</div>
<div class="q-title">QUOTATION</div>
<div class="ref-date"><span>${q.id}</span><span>DATE: ${dateStr}</span></div>
<div class="to-blk">TO,<br/><strong>Shri. ${q.customer_name}</strong><br/>${q.customer_address||''}</div>
<p>Dear Sir,</p>
<p>Greetings from <strong>SAKTHI INVERTERS!!!!</strong></p>
<p><strong>Subject: Offer for ${kw} kWp Solar Grid Tie Project</strong></p>
<div class="hl">Project Highlights:
  <ul>
    <li>Solar Panels &ndash; ${panelBrand}</li>
    <li>Inverter &nbsp;&nbsp;&nbsp;&nbsp;-- ${invBrand}</li>
  </ul>
</div>
<p>We appreciate your interest in renewable energy harvesting as well as <strong>SAKTHI INVERTERS</strong>... The company aims for delivering Renewable Energy Power Plants to its customers at a greater efficiency possible, with the help of modern Technologies and at a minimal cost.</p>
<div class="sec">Glossary</div>
<ul class="gls">
  <li><strong>Photovoltaic (PV):</strong> The physical effect of direct conversion of sunlight to electrical (Power)</li>
  <li><strong>PV Cell:</strong> The smallest element that generates electricity from sunlight.</li>
  <li><strong>PV Module:</strong> A collection of interconnected PV cells, encapsulated between glass &amp; back sheet. This is hermetically sealed unit.</li>
  <li><strong>String:</strong> Multiple PV modules connected in series electrically.</li>
  <li><strong>Mounting Structure:</strong> Structure used to hold modules in place, at desired angle and direction.</li>
  <li><strong>Cable:</strong> A conductor with one or more strands bound together, used for electrical power.</li>
  <li><strong>Junction Box:</strong> Inputs of several strings are connected to this box and taken as single output.</li>
  <li><strong>Lightning &amp; Surge Arrestor:</strong> Device used to protect all the components from lightning strikes and Voltage Surges.</li>
  <li><strong>Earthing:</strong> Described as a system of electrical connections to the general mass of earth.</li>
  <li><strong>Inverter:</strong> Electronic equipment for conversion of DC to conventional AC power.</li>
  <li><strong>PCU:</strong> (Power Conditioning Unit) which houses inverter + Charge controller.</li>
  <li><strong>Charge Controller:</strong> Electronic equipment which monitor &amp; control the charge &amp; Discharge of Battery.</li>
  <li><strong>DC Power:</strong> Direct current Power &ndash; PV Modules converts sunlight into DC Power.</li>
</ul>
<div class="sec">ON GRID INVERTER:</div>
<ul class="grd">
  <li>Next generation advanced power management systems.</li>
  <li>Produces true sine-wave AC Electricity for stand-alone use.</li>
  <li>Parallel, series and 3 phase stacking capabilities.</li>
  <li>Three phase configuration &ndash; option.</li>
  <li>Network communication.</li>
  <li>High surge power.</li>
</ul>
<div class="est-title">PROJECT ESTIMATE: (For ${kw}Kw)</div>
<table>
  <thead>
    <tr class="hrow"><th colspan="4">COST ESTIMATION</th></tr>
    <tr class="srow"><td colspan="4">${kw}KW BIFACIAL ${panelBrand}</td></tr>
    <tr class="hrow">
      <th style="width:50px">SLNO</th>
      <th style="width:160px">ITEMS</th>
      <th>Descriptions</th>
      <th style="width:120px">Quantity</th>
    </tr>
  </thead>
  <tbody>
    ${itemRows}
    <tr class="trow">
      <td colspan="3">Total Amount Including GST</td>
      <td>&#8377;${totalAmt.toLocaleString('en-IN')}/-</td>
    </tr>
    <tr>
      <td style="padding:8px;border:1px solid #ccc;text-align:center;vertical-align:top">12</td>
      <td style="padding:8px;border:1px solid #ccc;text-align:center;vertical-align:top;font-weight:500">Documentation, Installation commissioning</td>
      <td style="padding:8px;border:1px solid #ccc;vertical-align:top;font-size:11.5px">• KSEB Approval Including Statutory charges<br/>• Transportation, Loading Unloading<br/>• Installation, Cabling &amp; panel Mounting</td>
      <td style="padding:8px;border:1px solid #ccc;font-size:11px;vertical-align:top">Feasibility charge<br/>kseb: 1000<br/>Registration Charge: 1000+18%GST per KW</td>
    </tr>
  </tbody>
</table>
<div class="ebox">
  <strong>Project Estimate</strong><br/><br/>
  <strong>&#8377;${totalAmt.toLocaleString('en-IN')}/- INR ${amountInWords(totalAmt)}</strong> (WITHOUT KSEB CHARGES) (Including GST, Solar Panel Structure, Panel Erection, DC Wiring, installation commissioning)<br/>
  <strong style="color:#c00">(EXTRA COST FOR EXTRA FABRICATION AND INSTALLATION WILL BE CHARGED)</strong><br/><br/>
  Net Meter by KSEB<br/>
  <strong style="color:#c00">SUBSIDY AMOUNT: &#8377;${subsidyAmt.toLocaleString('en-IN')}/-</strong><br/>
  <strong style="color:#c00">EXTRA KSEB CHARGES</strong><br/>
  <strong style="color:#c00">KSEB CHARGES: &#8377;${ksebCharges.toLocaleString('en-IN')}/-</strong><br/>
  <strong style="color:#c00">FEASIBILITY CHARGE: &#8377;1000/-</strong>
</div>
<div class="bbox">
  <strong><u>BANK DETAILS</u></strong><br/>
  Ambalath enterprises<br/>
  A/c no: 1432 6511 0000 0541<br/>
  IFSC CODE: IBKL0001432<br/>
  IDBI BANK
</div>
<p>For any further clarifications please feel free to contact us.</p>
${q.valid_until ? `<p><strong>Validity:</strong> This quotation is valid until <strong>${fmtDate(q.valid_until)}</strong>.</p>` : '<p><strong>Validity:</strong> This quotation is valid for 30 days from the date of issue.</p>'}
<p>With Regards,</p>
<div class="sig"><strong>Authorised Signatory</strong><br/><br/><strong>SAKTHI INVERTERS</strong></div>
<div class="ftr">9446191276, 9846065984 &nbsp;|&nbsp; 0479 2305650, 2309650<br/>info@sakthiinverters.com &nbsp;|&nbsp; www.sakthiinverters.com</div>
<script>window.onload=function(){window.print();}</script>
</body></html>`;
  const win = window.open('', '_blank');
  win.document.write(html);
  win.document.close();
}

/* ── NORMAL PDF ────────────────────────────────────────────────── */
function printNormalQuotation(q) {
  const dateStr  = fmtDate(q.quote_date || (q.created_at || '').split('T')[0]);
  const { disc, total } = calcTotals(q);

  const itemRows = (q.items || []).map((item, i) => {
    const lineTotal = (Number(item.qty) || 1) * (Number(item.unit_price) || 0);
    const desc = (Number(item.qty) > 1) ? `${item.description} (${item.qty} nos)` : item.description;
    return `<tr>
      <td style="padding:8px;border:1px solid #ddd;text-align:center">${i+1}</td>
      <td style="padding:8px;border:1px solid #ddd">${desc||'—'}</td>
      <td style="padding:8px;border:1px solid #ddd;text-align:right;font-weight:600">&#8377;${lineTotal.toLocaleString('en-IN')}</td>
    </tr>`;
  }).join('');

  const totalRows = `
    ${disc > 0 ? `<tr><td colspan="2" style="padding:6px 8px;border:1px solid #ddd;text-align:right;color:#555">Discount</td><td style="padding:6px 8px;border:1px solid #ddd;text-align:right;color:#c00">-&#8377;${Math.round(disc).toLocaleString('en-IN')}</td></tr>` : ''}
    <tr style="background:#f3f4f6"><td colspan="2" style="padding:8px;border:1px solid #ddd;text-align:right;font-weight:700">Total</td><td style="padding:8px;border:1px solid #ddd;text-align:right;font-weight:700;font-size:14px">&#8377;${Math.round(total).toLocaleString('en-IN')}</td></tr>`;

  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"/>
<title>Quotation - ${q.customer_name}</title>
<style>
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:Arial,sans-serif;font-size:13px;color:#111;padding:30px 40px;max-width:800px;margin:0 auto}
.hdr{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #333;padding-bottom:14px;margin-bottom:20px}
.co{font-size:22px;font-weight:900;color:#1a2744;letter-spacing:1px}
.co-sub{font-size:12px;color:#555;margin-top:2px}
.co-right{text-align:right;font-size:11px;color:#333;line-height:1.7}
.date-line{text-align:right;margin-bottom:16px}
.to-blk{margin-bottom:14px;line-height:1.8}
p{margin-bottom:10px;line-height:1.7}
.stitle{font-weight:700;font-size:14px;margin:14px 0 7px}
ul{margin-left:20px;line-height:2}
table{width:100%;border-collapse:collapse;margin-bottom:14px}
thead tr{background:#1a2744;color:#fff}
th{padding:9px 8px;text-align:left;font-weight:500;border:1px solid #1a2744}
.note{background:#f9f9f9;border:1px solid #ddd;padding:12px 14px;margin-bottom:14px;border-radius:4px;font-size:12px}
.sig{margin-top:28px;text-align:right;line-height:2}
.ftr{margin-top:20px;padding-top:12px;border-top:2px solid #1a2744;display:flex;justify-content:space-between;font-size:10.5px;color:#555;line-height:1.8}
@media print{body{padding:15px 20px}}
</style></head><body>
<div class="hdr">
  <div><div class="co">SAKTHI INVERTERS</div><div class="co-sub">Since 1995</div></div>
  <div class="co-right"><strong>AMBALATH ENTERPRISES</strong><br/>GSTIN: 32AFNPN5476M1ZT<br/>East Nada, Sree Mahadevar Temple<br/>Kandiyoor, Mavelikara, Pin-690103</div>
</div>
<div class="date-line">Date: ${dateStr}</div>
<div class="to-blk">
  To,<br/>
  <strong>Mr. ${q.customer_name}</strong><br/>
  ${q.customer_address ? q.customer_address+'<br/>' : ''}
  ${q.customer_phone   ? q.customer_phone+'<br/>'   : ''}
</div>
<p><strong>Sub: Proposal for power backup system</strong></p>
<p><strong>Dear Sir,</strong></p>
<p>We are pleased to share the detailed quotation for your consideration. The document outlines the available inverter options, pricing, installation process, and service support. We remain committed to providing you with the best quality and reliable service. Please feel free to contact us for any clarifications or additional requirements.</p>
<p>We look forward to the opportunity to serve you.</p>
<div class="stitle">About Us</div>
<p>For over 30 years, SAKTHI has been the only company in Kerala dedicated exclusively to the inverter business. We do not deal with other products &ndash; our complete focus is on delivering premium inverters and reliable services.</p>
<p>Our Vision &ndash; To provide quality products at affordable prices while ensuring long-lasting customer satisfaction.</p>
<p>Our Promise &ndash; Value for money, professional service, and unmatched service network support.</p>
<div class="stitle">The SAKTHI Commitment</div>
<ul style="margin-bottom:14px">
  <li>Durable products engineered for performance</li>
  <li>Hassle-free service with standby support</li>
  <li>Affordable, transparent, and reliable solutions</li>
  <li>Professional care, wherever you are in Kerala</li>
</ul>
<div class="stitle">Product Description</div>
<table>
  <thead><tr><th style="width:50px">NO.</th><th>PRODUCT DESCRIPTION</th><th style="width:140px;text-align:right">PRICE</th></tr></thead>
  <tbody>${itemRows}${totalRows}</tbody>
</table>
<div class="note"><strong>Note:</strong><br/>The quoted amount is inclusive of all applicable taxes, transportation, and standard installation charges. Any additional charges arising from extra or extended wiring requirements or equipment will be billed separately.</div>
<div class="stitle">Installation:</div>
<p>The company is equipped with experienced technicians and service engineers. Installation is performed by technicians under the supervision of a service engineer. Fitting and wiring regarding the installation of this system will be carried out by the company.</p>
<div class="stitle">Delivery &amp; Payment Terms:</div>
<ul style="margin-bottom:14px">
  <li>The system will be delivered and installed soon after the confirmation of order subject to availability of stock.</li>
  <li>Heavy duty systems and solar systems will be delivered and installed at the site within 30 days of confirmation of the order.</li>
  <li>For heavy duty and solar systems, 50% of the total amount is to be paid in advance and balance payment as per payment schedule.</li>
  <li>No refund of advance amount under any circumstances of cancellation of the ordered product.</li>
</ul>
<div class="stitle">Validity:</div>
<p>Subject to the terms and conditions, we are pleased to keep our quotation open for your kind recognition for a period of 30 days from the date of issue of this proposal.</p>
<p>We deal with: SAKTHI, EXIDE, MICROTEK, POWEREX, V-GUARD, LUMINOUS &amp; JUMBO TROLLEYS</p>
<p>We assure you that our work will be executed with the highest standards of professionalism and quality, while remaining within the expected price range. This project will be treated with utmost priority, and we are fully committed to ensuring your complete satisfaction.</p>
<p><strong>Our mission is the paramount satisfaction of our customers.</strong></p>
<p>We look forward to building a long-term and trustworthy business relationship with you. Thanking you and assuring you of our best services always.</p>
<div class="sig">
  For Ambalath Enterprises<br/><br/><br/>
  S.Harikumar
</div>
<div class="ftr">
  <div><strong>Mobile:</strong> +91 9446191276, 9846065984, 9446428650, 9495819650<br/><strong>Landline:</strong> 0479-2305650, 0479-2309650</div>
  <div style="text-align:right"><strong>Website:</strong> www.sakthiinverters.com<br/><strong>Email:</strong> info@sakthiinverters.com | sakthiinverters@gmail.com</div>
</div>
<script>window.onload=function(){window.print();}</script>
</body></html>`;
  const win = window.open('', '_blank');
  win.document.write(html);
  win.document.close();
}

/* ── Dispatcher ────────────────────────────────────────────────── */
function printQuotation(q) {
  if (q.quote_type === 'solar') return printSolarQuotation(q);
  return printNormalQuotation(q);
}

/* ── Blank items ───────────────────────────────────────────────── */
const blankItem      = () => ({ product_id: '', description: '', qty: 1, unit_price: '' });
const blankSolarItem = () => ({ item_name: '', description: '', quantity: '' });

/* ── Main component ────────────────────────────────────────────── */
export default function Quotations() {
  const { canWrite, canDelete } = useAuth();
  const { show, Flash }         = useFlash();
  const [quotes, setQuotes]     = useState(loadAll);
  const [prods,  setProds]      = useState([]);

  const [showNew, setShowNew] = useState(false);
  const [viewQ,   setViewQ]   = useState(null);
  const [delQ,    setDelQ]    = useState(null);

  /* shared form */
  const [quoteType,  setQuoteType]  = useState('normal');
  const [custType,   setCustType]   = useState('existing');
  const [cFound,     setCFound]     = useState(null);
  const [manCust,    setManCust]    = useState({ name: '', phone: '', address: '' });
  const [quoteDate,  setQuoteDate]  = useState(today());
  const [saving,     setSaving]     = useState(false);

  /* normal-specific form */
  const [validUntil, setValid]    = useState('');
  const [items,      setItems]    = useState([blankItem()]);
  const [discVal,    setDiscVal]  = useState('');
  const [notes,      setNotes]    = useState('');

  /* solar-specific form */
  const [solarKw,        setSolarKw]        = useState('3');
  const [solarPanelBrand,setSolarPanelBrand]= useState('ADANI/WAAREE DCR PANELS');
  const [solarInvBrand,  setSolarInvBrand]  = useState('FOXESS/MICROTEK');
  const [solarTotal,     setSolarTotal]     = useState('');
  const [solarSubsidy,   setSolarSubsidy]   = useState('78000');
  const [solarKseb,      setSolarKseb]      = useState('3540');
  const [solarItems,     setSolarItems]     = useState(DEFAULT_SOLAR_ITEMS.map(x => ({ ...x })));

  useEffect(() => {
    inventoryApi.list().then(({ data }) => setProds(data || [])).catch(() => {});
  }, []);

  const draftQ = { items, disc_val: discVal };
  const { sub, disc, total } = calcTotals(draftQ);

  const resetForm = () => {
    setQuoteType('normal');
    setCustType('existing'); setCFound(null);
    setManCust({ name: '', phone: '', address: '' });
    setQuoteDate(today()); setValid('');
    setItems([blankItem()]); setDiscVal(''); setNotes('');
    setSolarKw('3'); setSolarPanelBrand('ADANI/WAAREE DCR PANELS');
    setSolarInvBrand('FOXESS/MICROTEK'); setSolarTotal('');
    setSolarSubsidy('78000'); setSolarKseb('3540');
    setSolarItems(DEFAULT_SOLAR_ITEMS.map(x => ({ ...x })));
  };

  /* normal item helpers */
  const setItemFields    = (idx, fields) => setItems(it => it.map((r, i) => i === idx ? { ...r, ...fields } : r));
  const handleProdSelect = (idx, prodId) => {
    if (!prodId) { setItemFields(idx, { product_id: '' }); return; }
    const p = prods.find(x => x.id === prodId);
    if (!p) return;
    const existingIdx = items.findIndex((r, i) => i !== idx && r.product_id === prodId);
    if (existingIdx !== -1) {
      setItems(it =>
        it.map((r, i) => i === existingIdx ? { ...r, qty: (Number(r.qty) || 1) + 1 } : r)
          .filter((_, i) => i !== idx)
      );
    } else {
      setItemFields(idx, { product_id: prodId, description: p.name, unit_price: p.mrp });
    }
  };
  const updItem   = (idx, key, val) => setItemFields(idx, { [key]: val });
  const addRow    = () => setItems(it => [...it, blankItem()]);
  const removeRow = idx => setItems(it => it.filter((_, i) => i !== idx));

  /* solar item helpers */
  const setSolarItemField = (idx, field, val) =>
    setSolarItems(it => it.map((r, i) => i === idx ? { ...r, [field]: val } : r));
  const addSolarRow    = () => setSolarItems(it => [...it, blankSolarItem()]);
  const removeSolarRow = idx => setSolarItems(it => it.filter((_, i) => i !== idx));

  const save = (andPrint = false) => {
    const custName = custType === 'existing' ? cFound?.full_name : manCust.name;
    if (!custName) { show('Please select or enter a customer.', 'error'); return; }
    if (quoteType === 'normal' && items.some(i => !i.description || i.unit_price === '')) {
      show('Fill description and price for every item row.', 'error'); return;
    }
    if (quoteType === 'solar' && (!solarTotal || Number(solarTotal) <= 0)) {
      show('Please enter the total project amount.', 'error'); return;
    }
    setSaving(true);
    const all = loadAll();
    const base = {
      created_at:       new Date().toISOString(),
      quote_date:       quoteDate,
      quote_type:       quoteType,
      customer_type:    custType,
      customer_id:      cFound?.id || null,
      customer_name:    custName,
      customer_phone:   custType === 'existing' ? (cFound?.phone || '') : manCust.phone,
      customer_address: custType === 'existing'
        ? [cFound?.address, cFound?.district].filter(Boolean).join(', ')
        : manCust.address,
      status: 'draft',
    };
    let q;
    if (quoteType === 'solar') {
      q = {
        ...base,
        id:                   nextSolarRefNo(),
        solar_kw:             solarKw,
        solar_panel_brand:    solarPanelBrand,
        solar_inverter_brand: solarInvBrand,
        solar_total_amount:   Number(solarTotal),
        solar_subsidy:        Number(solarSubsidy),
        solar_kseb_charges:   Number(solarKseb),
        solar_items:          solarItems,
        valid_until:          validUntil || null,
        items: [], disc_val: 0,
      };
    } else {
      q = {
        ...base,
        id: nextQuoteNo(all),
        items: items.map(i => ({
          product_id:  i.product_id || null,
          description: i.description,
          qty:         Number(i.qty) || 1,
          unit_price:  Number(i.unit_price) || 0,
        })),
        disc_type:   'fixed',
        disc_val:    Number(discVal) || 0,
        notes,
        valid_until: validUntil || null,
      };
    }
    const updated = [q, ...all];
    saveAll(updated); setQuotes(updated);
    show(`Quotation ${q.id} created!`);
    setShowNew(false); resetForm(); setSaving(false);
    if (andPrint) printQuotation(q);
  };

  const updateStatus = (id, status) => {
    const updated = quotes.map(x => x.id === id ? { ...x, status } : x);
    saveAll(updated); setQuotes(updated);
    setViewQ(v => v ? { ...v, status } : v);
    show('Status updated!');
  };

  const doDelete = () => {
    const updated = quotes.filter(x => x.id !== delQ.id);
    saveAll(updated); setQuotes(updated);
    setDelQ(null); show('Quotation deleted.');
  };

  const td       = today();
  const statDraf = quotes.filter(q => q.status === 'draft').length;
  const statSent = quotes.filter(q => q.status === 'sent').length;
  const statConv = quotes.filter(q => q.status === 'converted').length;

  const cellInput = (extra = {}) => ({
    width: '100%', border: 'none', outline: 'none',
    background: 'transparent', fontSize: 12, ...extra,
  });

  return (
    <div>
      <Flash/>
      <div className="page-hd">
        <h2>Quotations &amp; Estimates</h2>
        {canWrite && (
          <button className="btn btn-primary" onClick={() => { resetForm(); setShowNew(true); }}>
            <Plus s={14}/> New Quotation
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(4,1fr)' }}>
        <div className="stat-card"><div className="stat-lbl">Total</div><div className="stat-val">{quotes.length}</div></div>
        <div className="stat-card"><div className="stat-lbl">Draft</div><div className="stat-val" style={{ color: 'var(--text2)' }}>{statDraf}</div></div>
        <div className="stat-card"><div className="stat-lbl">Sent</div><div className="stat-val" style={{ color: 'var(--blue)' }}>{statSent}</div></div>
        <div className="stat-card"><div className="stat-lbl">Converted</div><div className="stat-val" style={{ color: 'var(--green)' }}>{statConv}</div></div>
      </div>

      {/* Quotes table */}
      <div className="card card-0">
        {quotes.length === 0
          ? <Empty msg="No quotations yet — click 'New Quotation' to create one!"/>
          : <div className="tbl-wrap"><table>
              <thead>
                <tr>
                  <th>Quote No</th><th>Type</th><th>Date</th>
                  <th>Customer</th><th>Total</th><th>Status</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {quotes.map(q => {
                  const isSolar = q.quote_type === 'solar';
                  const tot     = isSolar ? (q.solar_total_amount || 0) : calcTotals(q).total;
                  const expired = q.valid_until && q.valid_until < td;
                  return (
                    <tr key={q.id}>
                      <td style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--navy)', fontWeight: 600 }}>{q.id}</td>
                      <td>
                        <span style={{
                          fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 12,
                          background: isSolar ? '#fef9c3' : '#e0f2fe',
                          color:      isSolar ? '#854d0e' : '#0369a1',
                        }}>
                          {isSolar ? 'Solar' : 'Normal'}
                        </span>
                      </td>
                      <td style={{ fontSize: 12 }}>{fmtDate(q.quote_date || (q.created_at || '').split('T')[0])}</td>
                      <td style={{ fontWeight: 500 }}>{q.customer_name}</td>
                      <td style={{ fontWeight: 600 }}>₹{Math.round(tot).toLocaleString('en-IN')}</td>
                      <td><QPill status={q.status}/></td>
                      <td>
                        <div style={{ display: 'flex', gap: 5 }}>
                          <button className="btn btn-sm" onClick={() => setViewQ(q)}>View</button>
                          <button className="btn btn-sm btn-primary" title="Download PDF" onClick={() => printQuotation(q)}>
                            <Download s={12}/>
                          </button>
                          {canDelete && (
                            <button className="btn btn-sm btn-danger" title="Delete" onClick={() => setDelQ(q)}>
                              <Trash s={12}/>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table></div>
        }
      </div>

      {/* ── New Quotation Modal ─────────────────────────────────── */}
      <Modal
        open={showNew}
        onClose={() => setShowNew(false)}
        title="New Quotation / Estimate"
        footer={<>
          <button className="btn" onClick={() => setShowNew(false)}>Cancel</button>
          <button className="btn" onClick={() => save(false)} disabled={saving}>Save as Draft</button>
          <button className="btn btn-primary" onClick={() => save(true)} disabled={saving}>
            <Download s={13}/> Save &amp; Download PDF
          </button>
        </>}
      >
        <div style={{ fontSize: 13 }}>

          {/* Quote type */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontWeight: 600, fontSize: 11, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 7 }}>
              Quotation Type
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {[['normal','Normal (Inverter / Battery)'],['solar','Solar Grid-Tie']].map(([t,lbl]) => (
                <button key={t} className={`btn btn-sm${quoteType===t?' btn-primary':''}`} onClick={() => setQuoteType(t)}>
                  {lbl}
                </button>
              ))}
            </div>
          </div>

          {/* Customer */}
          <div style={{ marginBottom: 10, display: 'flex', gap: 6 }}>
            {[['existing','Existing Customer'],['manual','Walk-in / New Customer']].map(([t,lbl]) => (
              <button key={t} className={`btn btn-sm${custType===t?' btn-primary':''}`}
                onClick={() => { setCustType(t); setCFound(null); }}>
                {lbl}
              </button>
            ))}
          </div>

          {custType === 'existing'
            ? (!cFound
                ? <div style={{ marginBottom: 12 }}>
                    <CustomerSearch onSelect={c => setCFound(c)} placeholder="Search by name / phone / ID…"/>
                  </div>
                : <div style={{ marginBottom: 12, background: 'var(--surface)', borderRadius: 8, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div>
                      <div style={{ fontWeight: 700, color: 'var(--navy)', marginBottom: 2 }}>{cFound.full_name}</div>
                      {cFound.phone   && <div style={{ fontSize: 12, color: 'var(--text2)' }}>{cFound.phone}</div>}
                      {cFound.address && <div style={{ fontSize: 12, color: 'var(--text2)' }}>{[cFound.address,cFound.district].filter(Boolean).join(', ')}</div>}
                    </div>
                    <button className="btn btn-sm btn-ghost" style={{ padding: '2px 8px', flexShrink: 0 }} onClick={() => setCFound(null)}>Change</button>
                  </div>
              )
            : <div className="fg-2" style={{ marginBottom: 12 }}>
                <F label="Customer Name" full>
                  <input placeholder="Full Name" value={manCust.name} onChange={e => setManCust(m => ({ ...m, name: e.target.value }))}/>
                </F>
                <F label="Phone">
                  <input placeholder="Phone number" value={manCust.phone} onChange={e => setManCust(m => ({ ...m, phone: e.target.value }))}/>
                </F>
                <F label="Address" full>
                  <input placeholder="Address / District" value={manCust.address} onChange={e => setManCust(m => ({ ...m, address: e.target.value }))}/>
                </F>
              </div>
          }

          {/* Date */}
          <div className="fg-2" style={{ marginBottom: 12 }}>
            <F label="Quote Date">
              <input type="date" value={quoteDate} onChange={e => setQuoteDate(e.target.value)}/>
            </F>
            <F label="Valid Until">
              <input type="date" value={validUntil} onChange={e => setValid(e.target.value)}/>
            </F>
          </div>

          {/* ═══ SOLAR FORM ═══ */}
          {quoteType === 'solar' && (<>
            <div className="fg-2" style={{ marginBottom: 12 }}>
              <F label="System Capacity (kW)">
                <input type="number" min="1" placeholder="e.g. 3" value={solarKw} onChange={e => setSolarKw(e.target.value)}/>
              </F>
              <F label="Total Project Amount (₹)">
                <input type="number" min="0" placeholder="e.g. 225000" value={solarTotal} onChange={e => setSolarTotal(e.target.value)}/>
              </F>
            </div>
            <div className="fg-2" style={{ marginBottom: 12 }}>
              <F label="Panel Brand">
                <input placeholder="ADANI/WAAREE DCR PANELS" value={solarPanelBrand} onChange={e => setSolarPanelBrand(e.target.value)}/>
              </F>
              <F label="Inverter Brand">
                <input placeholder="FOXESS/MICROTEK" value={solarInvBrand} onChange={e => setSolarInvBrand(e.target.value)}/>
              </F>
            </div>
            <div className="fg-2" style={{ marginBottom: 14 }}>
              <F label="Subsidy Amount (₹)">
                <input type="number" value={solarSubsidy} onChange={e => setSolarSubsidy(e.target.value)}/>
              </F>
              <F label="KSEB Charges (₹)">
                <input type="number" value={solarKseb} onChange={e => setSolarKseb(e.target.value)}/>
              </F>
            </div>

            <div style={{ fontWeight: 600, fontSize: 11, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 7 }}>
              Project Items
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 7 }}>
              <thead>
                <tr style={{ background: 'var(--surface)' }}>
                  <th style={{ padding: '6px 4px', fontWeight: 500, color: 'var(--text2)', width: '4%', textAlign: 'center' }}>#</th>
                  <th style={{ padding: '6px 8px', fontWeight: 500, color: 'var(--text2)', width: '24%', textAlign: 'left' }}>Item Name</th>
                  <th style={{ padding: '6px 8px', fontWeight: 500, color: 'var(--text2)', width: '51%', textAlign: 'left' }}>Specifications</th>
                  <th style={{ padding: '6px 8px', fontWeight: 500, color: 'var(--text2)', width: '16%', textAlign: 'center' }}>Quantity</th>
                  <th style={{ width: '5%' }}></th>
                </tr>
              </thead>
              <tbody>
                {solarItems.map((item, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '4px 4px', color: 'var(--text2)', textAlign: 'center', fontSize: 11 }}>{idx+1}</td>
                    <td style={{ padding: '4px 6px' }}>
                      <input style={cellInput()} placeholder="e.g. Solar PV Module"
                        value={item.item_name} onChange={e => setSolarItemField(idx,'item_name',e.target.value)}/>
                    </td>
                    <td style={{ padding: '4px 6px' }}>
                      <textarea rows={3} style={{ ...cellInput(), resize: 'vertical', lineHeight: 1.5 }}
                        placeholder="Specifications…"
                        value={item.description} onChange={e => setSolarItemField(idx,'description',e.target.value)}/>
                    </td>
                    <td style={{ padding: '4px 6px' }}>
                      <input style={cellInput({ textAlign: 'center' })} placeholder="1 nos"
                        value={item.quantity} onChange={e => setSolarItemField(idx,'quantity',e.target.value)}/>
                    </td>
                    <td style={{ padding: '4px 4px', textAlign: 'center' }}>
                      {solarItems.length > 1 && (
                        <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red)', padding: 2 }}
                          onClick={() => removeSolarRow(idx)}>
                          <X s={13}/>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button className="btn btn-sm" onClick={addSolarRow}><Plus s={12}/> Add Item</button>
          </>)}

          {/* ═══ NORMAL FORM ═══ */}
          {quoteType === 'normal' && (<>
            <div style={{ fontWeight: 600, fontSize: 11, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 7 }}>
              Items / Products
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 7 }}>
              <thead>
                <tr style={{ background: 'var(--surface)' }}>
                  <th style={{ padding: '6px 8px', fontWeight: 500, color: 'var(--text2)', width: '36%', textAlign: 'left' }}>Product / Description</th>
                  <th style={{ padding: '6px 8px', fontWeight: 500, color: 'var(--text2)', width: '11%', textAlign: 'center' }}>Qty</th>
                  <th style={{ padding: '6px 8px', fontWeight: 500, color: 'var(--text2)', width: '22%', textAlign: 'right' }}>Unit Price (₹)</th>
                  <th style={{ padding: '6px 8px', fontWeight: 500, color: 'var(--text2)', width: '22%', textAlign: 'right' }}>Amount</th>
                  <th style={{ width: '9%' }}></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '4px 6px' }}>
                      <select
                        style={{ width: '100%', border: 'none', outline: 'none', background: 'transparent', fontSize: 11.5, color: 'var(--text2)', marginBottom: 3, cursor: 'pointer' }}
                        value={item.product_id || ''}
                        onChange={e => handleProdSelect(idx, e.target.value)}
                      >
                        <option value="">— Select from inventory —</option>
                        {prods.map(p => (
                          <option key={p.id} value={p.id}>
                            {p.name} (₹{Number(p.mrp).toLocaleString('en-IN')} | Stock: {p.stock_qty})
                          </option>
                        ))}
                      </select>
                      <input
                        style={cellInput({ borderTop: '1px dashed var(--border)', paddingTop: 3 })}
                        placeholder="Custom description (optional override)"
                        value={item.description}
                        onChange={e => updItem(idx,'description',e.target.value)}
                      />
                    </td>
                    <td style={{ padding: '4px 4px' }}>
                      <input type="number" min="1" style={cellInput({ textAlign: 'center' })}
                        value={item.qty} onChange={e => updItem(idx,'qty',e.target.value)}/>
                    </td>
                    <td style={{ padding: '4px 6px' }}>
                      <input type="number" min="0" style={cellInput({ textAlign: 'right' })}
                        placeholder="0.00" value={item.unit_price}
                        onChange={e => updItem(idx,'unit_price',e.target.value)}/>
                    </td>
                    <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 500 }}>
                      ₹{((Number(item.qty)||0)*(Number(item.unit_price)||0)).toLocaleString('en-IN',{maximumFractionDigits:0})}
                    </td>
                    <td style={{ padding: '4px 4px', textAlign: 'center' }}>
                      {items.length > 1 && (
                        <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red)', padding: 2, lineHeight: 1 }}
                          onClick={() => removeRow(idx)}>
                          <X s={13}/>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button className="btn btn-sm" onClick={addRow} style={{ marginBottom: 12 }}><Plus s={12}/> Add Row</button>

            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-start', marginBottom: 12 }}>
              <div style={{ flex: '1 1 200px' }}>
                <div style={{ fontWeight: 600, fontSize: 11, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 7 }}>Discount (₹)</div>
                <input type="number" min="0" step="0.01" placeholder="Fixed amount"
                  value={discVal}
                  style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 13 }}
                  onChange={e => setDiscVal(e.target.value)}/>
              </div>
              <div style={{ flex: '1 1 200px', background: 'var(--surface)', borderRadius: 8, padding: '12px 14px', fontSize: 12 }}>
                <div style={{ fontWeight: 600, fontSize: 11, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Summary</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5, color: 'var(--text2)' }}>
                  <span>Subtotal</span><span>₹{Math.round(sub).toLocaleString('en-IN')}</span>
                </div>
                {disc > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5, color: 'var(--text2)' }}>
                    <span>Discount</span><span>−₹{Math.round(disc).toLocaleString('en-IN')}</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 15, color: 'var(--navy)', borderTop: '1.5px solid var(--border)', paddingTop: 7, marginTop: 6 }}>
                  <span>Total</span><span>₹{Math.round(total).toLocaleString('en-IN')}</span>
                </div>
              </div>
            </div>

            <F label="Notes / Terms" full>
              <textarea rows={2} placeholder="Payment terms, conditions, or any notes…"
                value={notes} onChange={e => setNotes(e.target.value)}/>
            </F>
          </>)}
        </div>
      </Modal>

      {/* ── View Modal ──────────────────────────────────────────── */}
      <Modal
        open={!!viewQ}
        onClose={() => setViewQ(null)}
        title={`Quotation — ${viewQ?.id || ''}`}
        footer={<>
          <button className="btn" onClick={() => setViewQ(null)}>Close</button>
          <button className="btn btn-primary" onClick={() => viewQ && printQuotation(viewQ)}>
            <Download s={13}/> Download PDF
          </button>
        </>}
      >
        {viewQ && (() => {
          const isSolar  = viewQ.quote_type === 'solar';
          const vTot     = isSolar ? (viewQ.solar_total_amount || 0) : calcTotals(viewQ).total;
          const { sub: vs, disc: vd } = isSolar ? { sub: 0, disc: 0 } : calcTotals(viewQ);
          const vExpired = viewQ.valid_until && viewQ.valid_until < td;
          return (
            <div style={{ fontSize: 13 }}>
              <div style={{ marginBottom: 12 }}>
                <span style={{ fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 12,
                  background: isSolar ? '#fef9c3' : '#e0f2fe',
                  color:      isSolar ? '#854d0e' : '#0369a1' }}>
                  {isSolar ? 'Solar Grid-Tie Quotation' : 'Normal / Inverter Quotation'}
                </span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: 'var(--text2)' }}>Status:</span>
                  <QPill status={viewQ.status}/>
                </div>
                <select style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, border: '1px solid var(--border)', cursor: 'pointer' }}
                  value={viewQ.status} onChange={e => updateStatus(viewQ.id, e.target.value)}>
                  <option value="draft">Draft</option>
                  <option value="sent">Sent</option>
                  <option value="converted">Converted</option>
                  <option value="expired">Expired</option>
                </select>
              </div>

              <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 160px', background: 'var(--surface)', borderRadius: 8, padding: '10px 14px' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 6 }}>Quote To</div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--navy)', marginBottom: 3 }}>{viewQ.customer_name}</div>
                  {viewQ.customer_phone   && <div style={{ color: 'var(--text2)', fontSize: 12 }}>{viewQ.customer_phone}</div>}
                  {viewQ.customer_address && <div style={{ color: 'var(--text2)', fontSize: 12 }}>{viewQ.customer_address}</div>}
                </div>
                <div style={{ flex: '1 1 160px', background: 'var(--surface)', borderRadius: 8, padding: '10px 14px' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 6 }}>From</div>
                  <div style={{ fontWeight: 600, color: 'var(--navy)', marginBottom: 2 }}>Sakthi Inverters / Ambalath Enterprises</div>
                  <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.7 }}>
                    East Nada, Sree Mahadevar Temple,<br/>
                    Kandiyoor, Mavelikara - 690103<br/>
                    9446191276, 9846065984
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: 12, fontSize: 12, display: 'flex', gap: 16, flexWrap: 'wrap', color: 'var(--text2)' }}>
                <span>Quote Date: <strong style={{ color: '#111' }}>{fmtDate(viewQ.quote_date)}</strong></span>
                {isSolar && viewQ.solar_kw && <span>Capacity: <strong style={{ color: '#111' }}>{viewQ.solar_kw} kW</strong></span>}
                {viewQ.valid_until && (
                  <span>Valid Until: <strong style={{ color: vExpired ? 'var(--red)' : '#111' }}>{fmtDate(viewQ.valid_until)}</strong>
                    {vExpired && <span style={{ fontSize: 10, marginLeft: 5, background: 'var(--red)', color: '#fff', borderRadius: 3, padding: '1px 5px' }}>Expired</span>}
                  </span>
                )}
              </div>

              {isSolar ? (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 12 }}>
                  <thead>
                    <tr style={{ background: 'var(--navy)', color: '#fff' }}>
                      <th style={{ padding: '6px 8px', width: 36 }}>#</th>
                      <th style={{ padding: '6px 8px', width: 150 }}>Item</th>
                      <th style={{ padding: '6px 8px' }}>Description</th>
                      <th style={{ padding: '6px 8px', width: 100, textAlign: 'center' }}>Quantity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(viewQ.solar_items || []).map((item, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '6px 8px', color: 'var(--text2)', textAlign: 'center' }}>{i+1}</td>
                        <td style={{ padding: '6px 8px', fontWeight: 500 }}>{item.item_name}</td>
                        <td style={{ padding: '6px 8px', fontSize: 11.5, whiteSpace: 'pre-line' }}>{item.description}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'center' }}>{item.quantity}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 12 }}>
                  <thead>
                    <tr style={{ background: 'var(--navy)', color: '#fff' }}>
                      <th style={{ padding: '6px 8px' }}>#</th>
                      <th style={{ padding: '6px 8px' }}>Description</th>
                      <th style={{ padding: '6px 8px', textAlign: 'center' }}>Qty</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right' }}>Unit Price</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right' }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(viewQ.items||[]).map((item, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '6px 8px', color: 'var(--text2)', textAlign: 'center' }}>{i+1}</td>
                        <td style={{ padding: '6px 8px' }}>{item.description}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'center' }}>{item.qty}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right' }}>₹{Number(item.unit_price).toLocaleString('en-IN')}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 500 }}>₹{(item.qty*item.unit_price).toLocaleString('en-IN')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <div style={{ marginLeft: 'auto', maxWidth: 300, marginBottom: 12 }}>
                {isSolar ? (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 15, color: 'var(--navy)', borderTop: '1.5px solid var(--border)', paddingTop: 6 }}>
                      <span>Total (incl. GST)</span><span>₹{Math.round(vTot).toLocaleString('en-IN')}</span>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--red)', marginTop: 6, lineHeight: 1.8 }}>
                      Subsidy: −₹{Number(viewQ.solar_subsidy||78000).toLocaleString('en-IN')}<br/>
                      KSEB Charges: ₹{Number(viewQ.solar_kseb_charges||3540).toLocaleString('en-IN')} (extra)
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text2)', marginBottom: 4 }}>
                      <span>Subtotal</span><span>₹{Math.round(vs).toLocaleString('en-IN')}</span>
                    </div>
                    {vd > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text2)', marginBottom: 4 }}>
                        <span>Discount</span><span>−₹{Math.round(vd).toLocaleString('en-IN')}</span>
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 15, color: 'var(--navy)', borderTop: '1.5px solid var(--border)', paddingTop: 6, marginTop: 5 }}>
                      <span>Total</span><span>₹{Math.round(vTot).toLocaleString('en-IN')}</span>
                    </div>
                  </>
                )}
              </div>

              {viewQ.notes && (
                <div style={{ background: '#fffbeb', borderLeft: '3px solid #d97706', padding: '8px 12px', borderRadius: '0 6px 6px 0', fontSize: 12, color: '#555' }}>
                  <strong>Notes / Terms:</strong> {viewQ.notes}
                </div>
              )}
            </div>
          );
        })()}
      </Modal>

      {/* ── Delete confirm ──────────────────────────────────────── */}
      <Confirm
        open={!!delQ}
        onClose={() => setDelQ(null)}
        onOk={doDelete}
        msg={`Delete quotation ${delQ?.id}? This cannot be undone.`}
      />
    </div>
  );
}
